// BotUnanswered.tsx — the Unanswered screen (spec D5/M5 task-4, handoff §10 / "New
// Screens.dc.html"). Stacked rows, each with a 3px amber (`--st-paused`) left border — the
// handoff explicitly overrides the craft-floor default here. "Write answer" toggles an
// inline textarea editor in place; on success the editor is replaced with the VERBATIM
// success line from the handoff, and the row stays visible (see `useAnswerUnanswered`'s
// docstring for why the list itself is never invalidated on submit).
//
// Triage-state design (2026-08-21) Task 5 — segmented control `Open (n) · Dismissed ·
// Answered` (§C). Each tab is its OWN `useUnanswered` cache entry (status is part of the
// query key), so switching tabs shows that tab's own skeleton/empty state on first load
// rather than the previous tab's rows lingering while the new ones fetch. Row actions per
// tab: Open gets Write answer + Dismiss; Dismissed gets Reopen + Write answer (writing an
// answer from the Dismissed tab is still offered — the owner dismissed it as "not worth
// answering right now", not "never answer this"); Answered rows are read-only, linking to
// the Q&A source Write-answer created, or a muted "source removed" when that source was
// since deleted (`answeredSourceId` goes null via `SetNull`, spec §A).
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { CircleCheckIcon, EyeOffIcon, CheckCheckIcon, type LucideIcon } from 'lucide-react';
import { Button } from '@repo/ui/components/button';
import { EmptyState } from '@repo/ui/components/empty-state';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import type { UnansweredGroupDto, UnansweredStatus } from '@repo/shared-types';
import { PageHeader } from '../components/PageHeader';
import { useAnswerUnanswered, useSetUnansweredStatus, useUnanswered } from '../hooks/useUnanswered';
import { useBotOverview } from '../hooks/useBotOverview';
import { relativeTime } from '../lib/relative-time';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

/** Per-row editor state: absent = closed, `'open'` = editing, `'done'` = just answered
 * (renders the success line instead of the row's normal "Write answer" button). Keyed by
 * `normalized` — stable across a refetch even if `question`'s displayed casing changes. */
type RowState = 'open' | 'done';

function tabs(t: Catalog): { id: UnansweredStatus; label: string }[] {
  return [
    { id: 'open', label: t.unanswered.tabOpen },
    { id: 'dismissed', label: t.unanswered.tabDismissed },
    { id: 'answered', label: t.unanswered.tabAnswered },
  ];
}

// Spec §C's empty-state copy, verbatim, as the EmptyState TITLE — no separate description
// line (the design doc gives one line per tab, not a title+description pair).
function emptyStateTitle(t: Catalog): Record<UnansweredStatus, string> {
  return {
    open: t.unanswered.emptyTitleOpen,
    dismissed: t.unanswered.emptyTitleDismissed,
    answered: t.unanswered.emptyTitleAnswered,
  };
}

const EMPTY_STATE_ICON: Record<UnansweredStatus, LucideIcon> = {
  open: CircleCheckIcon,
  dismissed: EyeOffIcon,
  answered: CheckCheckIcon,
};

/** Rows at the real height/border-l shape they replace (handoff Shell Anatomy §06) — a neutral
 * `--muted` left border rather than the amber `--st-paused` one real rows carry, so the skeleton
 * doesn't read as "3 things need attention" before there's anything to attend to. */
function RowsSkeleton() {
  return (
    <div className="overflow-hidden rounded-[14px] border bg-card">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`flex items-center justify-between gap-3 border-l-[3px] border-l-muted px-5 py-4 ${i === 0 ? '' : 'border-t'}`}
        >
          <div className="flex flex-col gap-1.5">
            <SkeletonText className="w-64" />
            <SkeletonText className="w-40" />
          </div>
          <Skeleton className="h-8 w-28 shrink-0 rounded-md" />
        </div>
      ))}
    </div>
  );
}

function askedLabel(count: number, t: Catalog): string {
  return count === 1 ? t.unanswered.askedOnce : t.unanswered.askedTimes({ n: count });
}

/** Segmented control idiom shared across the dashboard (`SourceDetail.tsx`'s "Near-empty
 * only" toggle, `BotSources.tsx`'s add-source tab strip): `aria-pressed`, filled primary when
 * active, muted hover otherwise. */
function TabButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      {label}
      {count != null && ` (${count})`}
    </button>
  );
}

function AnsweredRow({
  botId,
  group,
  isFirst,
  t,
}: {
  botId: string | undefined;
  group: UnansweredGroupDto;
  isFirst: boolean;
  t: Catalog;
}) {
  return (
    <div className={`flex items-center justify-between gap-3 border-l-[3px] border-l-st-ready px-5 py-4 ${isFirst ? '' : 'border-t'}`}>
      <div className="flex flex-col gap-0.5">
        <span className="text-[14.5px] font-semibold">&ldquo;{group.question}&rdquo;</span>
        <span className="text-[12.5px] text-muted-foreground">
          {askedLabel(group.count, t)} · last {relativeTime(group.lastAskedAt, t.time)}
        </span>
      </div>
      {group.answeredSourceId ? (
        <Link
          to={`/bots/${botId}/sources/${group.answeredSourceId}`}
          className="flex-none text-[13px] font-medium text-primary hover:underline"
        >
          {t.unanswered.viewSourceCta}
        </Link>
      ) : (
        // `answeredSourceId` went null via `SetNull` — the Q&A source Write-answer created
        // was since deleted. The row survives (spec §A documented limitation); there's just
        // nowhere to send this link.
        <span className="flex-none text-[13px] text-muted-foreground">{t.unanswered.sourceRemoved}</span>
      )}
    </div>
  );
}

function UnansweredRow({
  botId,
  group,
  tab,
  isFirst,
  state,
  answer,
  onToggle,
  onAnswerChange,
  onSubmit,
  isSubmitting,
  error,
  onSetStatus,
  isStatusPending,
  t,
}: {
  botId: string | undefined;
  group: UnansweredGroupDto;
  tab: 'open' | 'dismissed';
  isFirst: boolean;
  state: RowState | undefined;
  answer: string;
  onToggle: () => void;
  onAnswerChange: (value: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  error?: string;
  onSetStatus: (status: 'open' | 'dismissed') => void;
  isStatusPending: boolean;
  t: Catalog;
}) {
  return (
    <div
      className={`flex flex-col gap-3 border-l-[3px] border-l-st-paused px-5 py-4 ${isFirst ? '' : 'border-t'}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[14.5px] font-semibold">&ldquo;{group.question}&rdquo;</span>
          <span className="text-[12.5px] text-muted-foreground">
            {askedLabel(group.count, t)} · last {relativeTime(group.lastAskedAt, t.time)}
            {' · '}
            {group.sampleConversationId ? (
              <Link
                to={`/bots/${botId}/conversations?open=${group.sampleConversationId}`}
                className="font-medium text-primary hover:underline"
              >
                {t.unanswered.viewChatCta}
              </Link>
            ) : (
              // The linked conversation was deleted (spec §A: the row survives, only the
              // link goes null via `SetNull`) — no route to send this to.
              <span>{t.unanswered.chatDeleted}</span>
            )}
          </span>
        </div>
        {state !== 'done' && (
          <div className="flex flex-none items-center gap-2">
            <Button type="button" size="sm" variant="outline" disabled={isStatusPending} onClick={() => onSetStatus(tab === 'open' ? 'dismissed' : 'open')}>
              {tab === 'open' ? t.unanswered.dismissCta : t.unanswered.reopenCta}
            </Button>
            <Button type="button" size="sm" onClick={onToggle}>
              {state === 'open' ? t.unanswered.cancelCta : t.unanswered.writeAnswerCta}
            </Button>
          </div>
        )}
      </div>

      {state === 'open' && (
        <div className="flex flex-col gap-2">
          {/* The placeholder says what to do rather than showing a sample answer: this box
              sits under a different question every time, so any example only fits the one
              question it was written for (it used to suggest a gift-wrapping price in ₫). */}
          <textarea
            rows={3}
            value={answer}
            onChange={(e) => onAnswerChange(e.target.value)}
            placeholder={t.unanswered.answerPlaceholder}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
          <div className="flex gap-2">
            <Button type="button" size="sm" disabled={!answer.trim() || isSubmitting} onClick={onSubmit}>
              {isSubmitting ? t.unanswered.addingCta : t.unanswered.addAsQaSourceCta}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onToggle}>
              {t.unanswered.cancelCta}
            </Button>
          </div>
          {error && <p className="text-[12.5px] text-destructive">{error}</p>}
        </div>
      )}

      {state === 'done' && (
        <div className="flex items-center gap-2 text-[13px] text-st-ready">
          <span className="font-semibold">✓</span> {t.unanswered.answerAddedNotice}
        </div>
      )}
    </div>
  );
}

export function BotUnanswered() {
  const t = useT();
  const TABS = tabs(t);
  const EMPTY_STATE_TITLE = emptyStateTitle(t);
  const { botId } = useParams<{ botId: string }>();
  const [tab, setTab] = useState<UnansweredStatus>('open');

  const { data, isPending, error } = useUnanswered(botId, tab);
  // Open count for the segmented control's "(n)" — reads the SAME overview aggregate the
  // rail's `CountPill` does (`totals.unansweredGroups`, `status: 'open'` server-side), rather
  // than the open tab's own list length, so the count next to "Open" stays right even while
  // a different tab is active and the open list isn't fetched at all.
  const { data: overview } = useBotOverview(botId);
  const answerMutation = useAnswerUnanswered(botId);
  const statusMutation = useSetUnansweredStatus(botId);

  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // `answerMutation` is ONE mutation object shared by every row (a single "Write answer"
  // editor is realistically open+submitting at a time, but nothing stops a visitor from
  // opening two) — `.isPending`/`.error` alone can't tell which row triggered them, so both
  // are tracked per-row here, keyed by `group.normalized` (SEV-3 #11/#6).
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<{ key: string; message: string } | null>(null);
  const [statusPendingId, setStatusPendingId] = useState<string | null>(null);

  // The server already filters to the active tab's `status` — no client-side filtering needed.
  const items = data?.items ?? [];

  function switchTab(next: UnansweredStatus) {
    if (next === tab) return;
    setTab(next);
    // A row editor open on the tab we're leaving has nothing to submit against once its rows
    // are out of view — closing pending state here avoids a stale "Cancel"/error carrying over
    // if the same `normalized` key ever appears on the new tab. `rowStates`/`drafts` reset too
    // (fix round 1, nit 6): without this, an editor left open (or a half-typed draft) on one
    // tab would still be "open" if a row with the same `normalized` key ever showed up on a
    // different tab later.
    setSubmittingKey(null);
    setSubmitError(null);
    setRowStates({});
    setDrafts({});
  }

  function toggle(key: string) {
    setRowStates((prev) => {
      const next = { ...prev };
      if (next[key] === 'open') delete next[key];
      else next[key] = 'open';
      return next;
    });
    // Closing (or reopening) the editor dismisses a stale error from a previous attempt on
    // this same row rather than leaving it stuck under a freshly-reopened blank textarea.
    setSubmitError((prev) => (prev?.key === key ? null : prev));
  }

  function submit(group: UnansweredGroupDto) {
    const answer = (drafts[group.normalized] ?? '').trim();
    if (!answer) return;
    setSubmitError(null);
    setSubmittingKey(group.normalized);
    answerMutation.mutate(
      { question: group.question, answer },
      {
        onSuccess: () => {
          setRowStates((prev) => ({ ...prev, [group.normalized]: 'done' }));
          setSubmittingKey(null);
        },
        onError: () => {
          setSubmittingKey(null);
          // Friendly, generic copy (per design voice) rather than the raw API error —
          // matches how top-level query errors on this same screen are NOT how mutation
          // errors should read; details of what went wrong belong in server logs.
          setSubmitError({ key: group.normalized, message: t.unanswered.saveAnswerError });
        },
      },
    );
  }

  function setStatus(group: UnansweredGroupDto, status: 'open' | 'dismissed') {
    setStatusPendingId(group.id);
    statusMutation.mutate(
      { id: group.id, status },
      { onSettled: () => setStatusPendingId(null) },
    );
  }

  // Task 7 header subline: how many questions are waiting, the fact this screen exists to
  // surface. Reads the same `items` the rows below render from, so it agrees with what's on
  // screen — including a just-answered row still showing its success line (per
  // `useAnswerUnanswered`'s doc comment, this list is deliberately not invalidated on submit).
  const subtitleVerb =
    tab === 'open'
      ? t.unanswered.subtitleVerbOpen
      : tab === 'dismissed'
        ? t.unanswered.subtitleVerbDismissed
        : t.unanswered.subtitleVerbAnswered;
  // While the list is in flight the subline keeps its height — via `PageHeader`'s own
  // `subtitleLoading` (subtitle-skeleton spec §C, 2026-08-22), not a raw Skeleton passed as
  // `subtitle` — so the filter tabs below don't jump up and back down on every load (Johnny,
  // 2026-08-22).
  const headerSubtitle = error
    ? undefined
    : t.unanswered.headerSubtitle({ n: items.length, verb: subtitleVerb });

  const emptyTitle = EMPTY_STATE_TITLE[tab];
  const EmptyIcon = EMPTY_STATE_ICON[tab];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t.unanswered.pageTitle} subtitle={headerSubtitle} subtitleLoading={isPending} />

      <div className="flex gap-1" role="group" aria-label={t.unanswered.filterByStatusAriaLabel}>
        {TABS.map((tabDef) => (
          <TabButton
            key={tabDef.id}
            label={tabDef.label}
            count={tabDef.id === 'open' ? overview?.totals.unansweredGroups : undefined}
            active={tab === tabDef.id}
            onClick={() => switchTab(tabDef.id)}
          />
        ))}
      </div>

      {isPending && (
        <div className="max-w-[860px]">
          <RowsSkeleton />
        </div>
      )}
      {error && <p className="text-destructive">{error.message}</p>}

      {!isPending && !error && (
        <div className="flex max-w-[860px] flex-col gap-4">
          {tab === 'open' && (
            <p className="text-[13.5px] text-muted-foreground">{t.unanswered.openTabIntro}</p>
          )}

          {items.length === 0 ? (
            <EmptyState icon={EmptyIcon} title={emptyTitle} />
          ) : (
            <div className="overflow-hidden rounded-[14px] border bg-card">
              {items.map((group, i) =>
                tab === 'answered' ? (
                  <AnsweredRow key={group.normalized} botId={botId} group={group} isFirst={i === 0} t={t} />
                ) : (
                  <UnansweredRow
                    key={group.normalized}
                    botId={botId}
                    group={group}
                    tab={tab as 'open' | 'dismissed'}
                    isFirst={i === 0}
                    state={rowStates[group.normalized]}
                    answer={drafts[group.normalized] ?? ''}
                    onToggle={() => toggle(group.normalized)}
                    onAnswerChange={(value) => setDrafts((prev) => ({ ...prev, [group.normalized]: value }))}
                    onSubmit={() => submit(group)}
                    isSubmitting={submittingKey === group.normalized}
                    error={submitError?.key === group.normalized ? submitError.message : undefined}
                    onSetStatus={(status) => setStatus(group, status)}
                    isStatusPending={statusPendingId === group.id}
                    t={t}
                  />
                ),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
